import React, { useMemo } from 'react';
import { useNavigationContainerRef } from 'expo-router';

import { useResponsive } from '@sg/garnish';

import { Navbar } from '../../../../../components';
import {
  CONTENT_WITH_BG_COLOR_STYLES,
  CONTENT_WITHOUT_BG_STYLES,
  DEFAULT_SCREEN_OPTIONS,
  type ScreenOptions,
  type ScreenOptionsMap,
} from '../../../../constants';

// ─────────────────────────────────────────────────────────────────────────────

export const useTabStackOptions = () => {
  const { match } = useResponsive();
  const navRef = useNavigationContainerRef();

  return useMemo(() => {
    return {
      //
      // ─── Main Stack Options (applied to all screens) ─────────────────────

      Stack: { ...DEFAULT_SCREEN_OPTIONS, headerShown: false },

      // ─── Individual Tabs ─────────────────────────────────────────────────

      Tabs: { ...DEFAULT_SCREEN_OPTIONS },
      Modals: (): ScreenOptions => {
        const currentScreenOptions = navRef.getCurrentOptions();
        const castedScreenOptions = currentScreenOptions as Record<string, unknown>;

        return {
          presentation: match(['modal', 'transparentModal']),

          /**
           * `Modals` stack content styles apply to all nested screens. Some screens require
           * transparent content, so they can opt in with the `withTransparentContentBg` screen option.
           */
          contentStyle: castedScreenOptions?.withTransparentContentBg
            ? CONTENT_WITHOUT_BG_STYLES
            : match([CONTENT_WITH_BG_COLOR_STYLES, CONTENT_WITHOUT_BG_STYLES]),
        };
      },
      Auth: { ...DEFAULT_SCREEN_OPTIONS, presentation: 'modal' },

      // ─── Not Found ─────────────────────────────────────────────────────

      NotFound: {
        ...DEFAULT_SCREEN_OPTIONS,
        headerShown: match([false, true]),
        header: match([undefined, renderNavbar]),
      },
    } satisfies TabStackOptionsMap;
  }, [match, navRef]);
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function renderNavbar() {
  return <Navbar />;
}

// ─── Types ───────────────────────────────────────────────────────────────────

type ScreenOptionsCallback = () => ScreenOptions;
type TabStackOptionsMap = Record<string, ScreenOptionsMap[string] | ScreenOptionsCallback>;
